import React from 'react';
import { FaChevronDown, FaHistory, FaLeaf, FaStore } from 'react-icons/fa';
import { MyStoresCard } from '../components/MyStoresCard';
import SeedFeedFinder from '../components/SeedFeedFinder';
import { YPagination } from '../../../ui/YPagination';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { buildPlantingLogRow } from './plantingLogUtils';

const STORE_HISTORY_KEY = 'y-area-store-history';
const LOG_PAGE_SIZE = 5;

function toFinderSeedOptions(presets) {
  return (Array.isArray(presets) ? presets : [])
    .map((preset) => ({
      value: String(preset.value || preset.id || '').trim(),
      label: preset.label || preset.name || String(preset.value || preset.id || ''),
    }))
    .filter((option) => option.value);
}

function PlantingLogPanel({
  events = [],
  savedGroups = [],
  plansReady = true,
  loading = false,
  onOpenEvent,
  onDeleteEvent,
}) {
  const rows = React.useMemo(
    () => (Array.isArray(events) ? events : []).map((event) => (
      buildPlantingLogRow(event, savedGroups, { plansReady })
    )),
    [events, savedGroups, plansReady]
  );
  const paginationKey = `${rows.length}:${plansReady ? 'ready' : 'loading'}`;
  const {
    items: pageRows,
    offset,
    limit,
    onPrev,
    onNext,
  } = useClientPagination(rows, LOG_PAGE_SIZE, paginationKey);

  if (loading && rows.length === 0) {
    return <p className="plant-plans-log-empty">Loading plantings…</p>;
  }
  if (rows.length === 0) {
    return <p className="plant-plans-log-empty">No plantings logged yet.</p>;
  }

  return (
    <>
      <ul className="plant-plans-log-list" data-testid="plant-plans-log-list">
        {pageRows.map((row) => (
          <li key={row.id} className="plant-plans-log-item">
            <button
              type="button"
              className={`plant-plans-log-row${row.missing ? ' is-missing' : ''}`}
              data-testid={`plant-plans-log-row-${row.id}`}
              onClick={() => onOpenEvent?.(row.event)}
            >
              <span className="plant-plans-log-row__icon" aria-hidden="true">
                <FaLeaf />
              </span>
              <span className="plant-plans-log-row__copy">
                <span className="plant-plans-log-row__title">{row.title}</span>
                <span className="plant-plans-log-row__meta">{row.meta}</span>
              </span>
              {row.missing ? (
                <span className="plant-plans-log-row__badge">PLAN REMOVED</span>
              ) : (
                <span className="plant-plans-log-row__open">VIEW PLAN</span>
              )}
            </button>
            <button
              type="button"
              className="plant-plans-log-row__delete"
              data-testid={`plant-plans-log-delete-${row.id}`}
              aria-label={`Remove ${row.title} from the planting log`}
              onClick={() => onDeleteEvent?.(row.event)}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
      <YPagination
        offset={offset}
        limit={limit}
        count={rows.length}
        onPrev={onPrev}
        onNext={onNext}
      />
    </>
  );
}

/**
 * Feed Tracker–style collapsibles on My Plans: find seed stores + planting log.
 */
export default function PlantingLibraryStrips({
  seedOptions = [],
  plantingEvents = [],
  savedGroups = [],
  plansReady = true,
  plantingEventsLoading = false,
  onOpenPlantingEvent,
  onDeletePlantingEvent,
}) {
  const [storesOpen, setStoresOpen] = React.useState(false);
  const [logOpen, setLogOpen] = React.useState(false);
  const seedFinderOptions = React.useMemo(
    () => toFinderSeedOptions(seedOptions),
    [seedOptions],
  );

  return (
    <div className="plant-plans-expand-strips" data-testid="plant-plans-expand-strips">
      <div className="plant-plans-strip" data-testid="plant-plans-stores-strip">
        <button
          type="button"
          className={`plant-plans-strip__toggle${storesOpen ? ' is-open' : ''}`}
          aria-expanded={storesOpen}
          aria-controls="plant-plans-stores-panel"
          onClick={() => setStoresOpen((open) => !open)}
          data-testid="plant-plans-stores-toggle"
        >
          <FaStore aria-hidden="true" />
          <span>FIND SEED STORES</span>
          <FaChevronDown
            className={`plant-plans-strip__chevron${storesOpen ? ' is-open' : ''}`}
            aria-hidden="true"
          />
        </button>
        {storesOpen ? (
          <div
            id="plant-plans-stores-panel"
            className="plant-plans-strip__panel"
            data-testid="plant-plans-stores-panel"
          >
            <section className="plant-asst-stores-panel">
              <div className="plant-asst-stores-panel__header">
                <FaStore aria-hidden="true" />
                <span>My stores</span>
              </div>
              <div className="plant-asst-stores-panel__body">
                <MyStoresCard appearance="plant-asst" />
              </div>
            </section>
            <section className="plant-asst-stores-panel plant-asst-stores-panel--finder">
              <div className="plant-asst-stores-panel__header">
                <FaStore aria-hidden="true" />
                <span>Find feed &amp; seed stores</span>
              </div>
              <div className="plant-asst-stores-panel__body">
                <SeedFeedFinder
                  appearance="plant-asst"
                  variant="area"
                  seedOptions={seedFinderOptions}
                  defaultRadius={25}
                  storageKey={STORE_HISTORY_KEY}
                />
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <div className="plant-plans-strip" data-testid="plant-plans-log-strip">
        <button
          type="button"
          className={`plant-plans-strip__toggle${logOpen ? ' is-open' : ''}`}
          aria-expanded={logOpen}
          aria-controls="plant-plans-log-panel"
          onClick={() => setLogOpen((open) => !open)}
          data-testid="plant-plans-log-toggle"
        >
          <FaHistory aria-hidden="true" />
          <span>PLANTING LOG</span>
          <FaChevronDown
            className={`plant-plans-strip__chevron${logOpen ? ' is-open' : ''}`}
            aria-hidden="true"
          />
        </button>
        {logOpen ? (
          <div
            id="plant-plans-log-panel"
            className="plant-plans-strip__panel"
            data-testid="plant-plans-log-panel"
          >
            <PlantingLogPanel
              events={plantingEvents}
              savedGroups={savedGroups}
              plansReady={plansReady}
              loading={plantingEventsLoading}
              onOpenEvent={onOpenPlantingEvent}
              onDeleteEvent={onDeletePlantingEvent}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
